/**
 * Formula Validator
 *
 * Validates a formula AST against a specific data source,
 * checking that all referenced fields exist and are valid.
 */

import type {
  FormulaASTNode,
  FormulaValidationResult,
  FormulaValidationError,
  ReportDataSource,
  FormulaFieldDefinition,
} from '@mesterplan/shared-types';
import {
  FORMULA_FUNCTIONS,
  FORMULA_FIELDS_BY_DATA_SOURCE,
  getAllPrefixedFields,
} from '@mesterplan/shared-types';
import { parseFormula, FormulaParserError } from './parser';
import { FormulaTokenizerError } from './tokenizer';

const VALID_FUNCTIONS = new Set<string>(FORMULA_FUNCTIONS);

// Build set of all valid prefixed field names for cross-source mode
const ALL_PREFIXED_FIELDS = new Set<string>(
  getAllPrefixedFields().map((f: FormulaFieldDefinition) => f.name)
);

/**
 * Validate a formula expression for a given data source.
 * Use dataSource 'all' for cross-source formulas with prefixed field names.
 */
export function validateFormula(
  expression: string,
  dataSource: ReportDataSource | 'all'
): FormulaValidationResult {
  const errors: FormulaValidationError[] = [];
  const warnings: string[] = [];
  const usedFields: string[] = [];
  const usedFunctions: string[] = [];

  if (!expression.trim()) {
    return {
      isValid: false,
      errors: [{ message: 'Formlen er tom', position: 0, length: 1 }],
      warnings: [],
      ast: null,
      usedFields: [],
      usedFunctions: [],
    };
  }

  // Try to parse the formula
  let ast: FormulaASTNode | null = null;
  try {
    ast = parseFormula(expression);
  } catch (error) {
    if (error instanceof FormulaParserError) {
      errors.push({
        message: error.message,
        position: error.position,
        length: error.length,
      });
    } else if (error instanceof FormulaTokenizerError) {
      errors.push({
        message: error.message,
        position: error.position,
        length: 1,
      });
    } else {
      errors.push({
        message: 'Ukendt fejl ved parsing af formel',
        position: 0,
        length: expression.length,
      });
    }

    return {
      isValid: false,
      errors,
      warnings,
      ast: null,
      usedFields: [],
      usedFunctions: [],
    };
  }

  // Get valid fields based on data source mode
  let validFields: Set<string>;
  if (dataSource === 'all') {
    // Cross-source mode: use prefixed field names (e.g., projects.actualRevenue)
    validFields = ALL_PREFIXED_FIELDS;
  } else {
    // Single source mode: use simple field names
    const fields = FORMULA_FIELDS_BY_DATA_SOURCE[dataSource] || [];
    validFields = new Set<string>(fields.map((f: FormulaFieldDefinition) => f.name));
  }

  // Validate the AST
  validateNode(ast, validFields, errors, warnings, usedFields, usedFunctions);

  // Check if any aggregate functions are used
  if (usedFunctions.length === 0 && usedFields.length > 0) {
    warnings.push('Formlen bruger felter uden aggregeringsfunktioner (SUM, AVG, etc.)');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    ast,
    usedFields: [...new Set(usedFields)],
    usedFunctions: [...new Set(usedFunctions)],
  };
}

function validateNode(
  node: FormulaASTNode,
  validFields: Set<string>,
  errors: FormulaValidationError[],
  warnings: string[],
  usedFields: string[],
  usedFunctions: string[]
): void {
  switch (node.type) {
    case 'number':
      // Numbers are always valid
      break;

    case 'field':
      usedFields.push(node.name);
      if (!validFields.has(node.name)) {
        errors.push({
          message: `Ukendt felt: "${node.name}"`,
          position: 0,
          length: node.name.length,
        });
      }
      break;

    case 'function_call':
      usedFunctions.push(node.name);
      if (!VALID_FUNCTIONS.has(node.name)) {
        errors.push({
          message: `Ukendt funktion: "${node.name}"`,
          position: 0,
          length: node.name.length,
        });
      }
      // Validate the argument
      validateNode(node.argument, validFields, errors, warnings, usedFields, usedFunctions);
      break;

    case 'binary_op':
      validateNode(node.left, validFields, errors, warnings, usedFields, usedFunctions);
      validateNode(node.right, validFields, errors, warnings, usedFields, usedFunctions);
      break;

    case 'unary_op':
      validateNode(node.operand, validFields, errors, warnings, usedFields, usedFunctions);
      break;
  }
}

/**
 * Get all fields used in a formula
 */
export function getFormulaFields(ast: FormulaASTNode): string[] {
  const fields: string[] = [];
  collectFields(ast, fields);
  return [...new Set(fields)];
}

function collectFields(node: FormulaASTNode, fields: string[]): void {
  switch (node.type) {
    case 'field':
      fields.push(node.name);
      break;
    case 'function_call':
      collectFields(node.argument, fields);
      break;
    case 'binary_op':
      collectFields(node.left, fields);
      collectFields(node.right, fields);
      break;
    case 'unary_op':
      collectFields(node.operand, fields);
      break;
  }
}

/**
 * Get all functions used in a formula
 */
export function getFormulaFunctions(ast: FormulaASTNode): string[] {
  const functions: string[] = [];
  collectFunctions(ast, functions);
  return [...new Set(functions)];
}

function collectFunctions(node: FormulaASTNode, functions: string[]): void {
  switch (node.type) {
    case 'function_call':
      functions.push(node.name);
      collectFunctions(node.argument, functions);
      break;
    case 'binary_op':
      collectFunctions(node.left, functions);
      collectFunctions(node.right, functions);
      break;
    case 'unary_op':
      collectFunctions(node.operand, functions);
      break;
  }
}
